import React, { useEffect } from 'react';

import { useQuery } from '@apollo/client/react';
import Honeybadger from '@honeybadger-io/js';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Divider,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';
import { DateTime } from 'luxon';

import { GET_PROGRAM_CART_BLUEPRINTS } from '../../graphql-operations/catalog-service/programs.query';
import {
  ProgramCartBlueprintsQuery,
  ProgramCartBlueprintsQueryVariables,
} from '../../ts/@types/generated/graphql/catalog-service/graphql';
import { ClientNames } from '../../utils/apolloClient';
import { safeProductImageUrl } from '../../utils/safe-product-image-url';
import FrequentlyAskedQuestions from './FrequentyAskedQuestions';
import { MetroDetails } from './index';
import Custom404 from '../../pages/404';
import EstimatedDeliveryTooltip from '../EstimatedDeliveryTooltip';
import { mapProgramDeliveriesToUpcomingDeliveryDates } from '../../utils/map-program-deliveries-to-upcoming-delivery-dates';

const CatalogPreview: React.FC<{
  programId: number;
  metroDetails: MetroDetails;
  tenantId: number;
  closeModal: () => void;
}> = ({ programId, metroDetails, tenantId, closeModal }) => {
  const theme = useTheme();

  const {
    data,
    error: getProgramError,
    loading: getProgramLoading,
  } = useQuery<ProgramCartBlueprintsQuery, ProgramCartBlueprintsQueryVariables>(
    GET_PROGRAM_CART_BLUEPRINTS,
    {
      variables: {
        input: {
          filter: {
            ids: [programId],
            tenantIds: [tenantId],
            sellable: true,
          },
          limit: 1,
        },
        metroId: metroDetails.id,
        deliveryWeek: metroDetails.deliveryWeek,
      },
      context: {
        clientName: ClientNames.CATALOG_SERVICE_GRAPH,
      },
      fetchPolicy: 'no-cache',
    },
  );

  useEffect(() => {
    if (
      !getProgramLoading &&
      !data?.programs[0].programMetros[0]?.cartBlueprints[0]
    ) {
      Honeybadger.notify({
        name: 'Missing cart blueprints for program',
        context: {
          programId,
          metroId: metroDetails.id,
          deliveryWeek: metroDetails.deliveryWeek,
        },
      });
    }
  }, [data, getProgramLoading]);

  if (getProgramLoading) {
    return <CircularProgress />;
  }

  if (getProgramError) {
    return (
      <Grid
        container
        sx={{
          px: 1,
          pt: 1,
        }}
      >
        <Grid>
          <Alert severity={'error'} sx={{ width: '100%' }}>
            Something went wrong. Please try again later.
          </Alert>
        </Grid>
      </Grid>
    );
  }

  const program = data?.programs[0];
  if (!program) {
    return <Custom404 />;
  }

  const programMetro = program.programMetros[0];
  if (!program) {
    return <Custom404 />;
  }

  const deliveriesPerWeek = programMetro.deliveries.length;
  const totalDeliveries = program.durationInWeeks * deliveriesPerWeek;
  const cartBlueprint = programMetro.cartBlueprints[0];

  const upcomingDeliveries = mapProgramDeliveriesToUpcomingDeliveryDates(
    programMetro.deliveries.map((pm) => pm.day),
    metroDetails.upcomingDeliveryDates,
    metroDetails.estimatedTransitTime,
  );

  const deliveryDetails = {
    type: metroDetails.type,
    upcomingDeliveries,
  };

  if (!cartBlueprint) {
    return (
      <Grid
        container
        sx={{
          px: 1,
          pt: 1,
        }}
      >
        <Grid sx={{ width: '100%' }}>
          <Alert severity={'info'}>
            The menu is not yet available. Please check back later.
          </Alert>
        </Grid>
        <Grid sx={{ marginTop: '1rem' }}>
          <FrequentlyAskedQuestions
            deliveriesPerWeek={deliveriesPerWeek}
            deliveryDetails={deliveryDetails}
            totalDeliveries={totalDeliveries}
          />
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      sx={{
        px: 1,
        pt: 1,
      }}
    >
      {cartBlueprint?.deliveries.map((deliveryBlueprint, idx) => {
        const metroDelivery = deliveryDetails.upcomingDeliveries.find(
          (upcomingDelivery) => {
            return (
              upcomingDelivery.deliveryDate === deliveryBlueprint.deliveryDate
            );
          },
        );
        if (!metroDelivery) {
          throw new Error(
            'Failed to match program cart delivery blueprint to metro delivery',
          );
        }

        const formattedDeliveryDate = DateTime.fromISO(
          metroDelivery.deliveryDate,
          { zone: 'utc' },
        ).toFormat('EEE, MMM d');
        const formattedEstimatedDeliveryDate =
          metroDelivery.estimatedDeliveryDate
            ? DateTime.fromISO(metroDelivery.estimatedDeliveryDate, {
                zone: 'utc',
              }).toFormat('EEE, MMM d')
            : undefined;

        return (
          <Grid key={deliveryBlueprint.deliveryDate}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="overline">
                {formattedEstimatedDeliveryDate && <>Est. </>}
                Delivery {idx + 1} on
              </Typography>
              {formattedEstimatedDeliveryDate && (
                <EstimatedDeliveryTooltip fontSize="16px" />
              )}
            </Box>
            <Typography
              variant="body1"
              sx={{
                color: 'primary.dark',
              }}
            >
              {formattedEstimatedDeliveryDate || formattedDeliveryDate}
            </Typography>
            <Divider sx={{ marginBottom: '1rem' }} />
            <Grid container>
              {deliveryBlueprint.lineItems.map((lineItem) => {
                const itemTitle = `${lineItem.variant.product.title} ${lineItem.variant.product.subtitle}`;

                return (
                  <a
                    key={itemTitle}
                    href={`/meals/${lineItem.variant.product.gardenMealId}`}
                    target={'_blank'}
                    style={{
                      width: '100%',
                      textDecoration: 'inherit',
                      color: 'inherit',
                    }}
                    rel="noreferrer"
                  >
                    <Card
                      sx={{ display: 'flex', marginBottom: '0.5rem' }}
                      elevation={0}
                    >
                      <CardMedia
                        component="img"
                        sx={{ width: '9.5rem' }}
                        image={safeProductImageUrl(
                          lineItem.variant.product.imageUrl,
                        )}
                        alt={`Image of ${itemTitle}`}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          backgroundColor: theme.palette.grey[50],
                          width: '100%',
                        }}
                      >
                        <CardContent sx={{ flex: '1 0 auto' }}>
                          <Typography
                            component="div"
                            variant="subtitle2"
                            sx={{ textAlign: 'left' }}
                          >
                            {itemTitle}
                          </Typography>
                        </CardContent>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'start',
                            pl: 1,
                            pb: 1,
                          }}
                        >
                          <ButtonGroup
                            disableElevation
                            size="small"
                            variant="contained"
                          >
                            <Typography
                              variant={'subtitle2'}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 0.5rem',
                              }}
                            >
                              Quantity: {lineItem.quantity}
                            </Typography>
                          </ButtonGroup>
                        </Box>
                      </Box>
                    </Card>
                  </a>
                );
              })}
            </Grid>
          </Grid>
        );
      })}
      <Grid sx={{ marginTop: '1rem' }}>
        <FrequentlyAskedQuestions
          deliveriesPerWeek={deliveriesPerWeek}
          deliveryDetails={deliveryDetails}
          totalDeliveries={totalDeliveries}
        />
      </Grid>
      <Button
        color="primary"
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        onClick={closeModal}
        sx={{
          minWidth: '6rem',
          paddingLeft: '0.5rem',
          marginTop: '2rem',
        }}
      >
        Return to checkout
      </Button>
    </Grid>
  );
};

export default CatalogPreview;
